import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { planApi, subscriptionApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { isOwner } from '../utils/roles';
import { buttonClass } from './settings/SettingsUI';
import { GiftIcon } from './icons/LucideIcons';
import { formatMoney } from './settings/planBilling';

/** '2026-11-15' → 'November 15, 2026' (the date as written, no timezone shift). */
export function formatTrialEnd(endsOn) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(endsOn || '');
  if (!m) return endsOn || '';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// "Choose a plan" opens the plan picker on Settings → Plan & billing (B3).
export const CHOOSE_PLAN_PATH = '/settings?tab=plan&choose=1';

/**
 * Self-serve trial (10B): from 7 days before a trial ends, the shop's owners
 * see when it ends at the top of every page, with "Choose a plan" (QA A F17:
 * no "contact us" beside the self-serve button).
 * GET /api/plan sends `trial` only for a shop on trial, with showBanner set
 * from 7 days out; every other shop (Twigs, paying shops) and every non-owner
 * renders nothing. "Choose a plan" opens the plan picker on Settings → Plan &
 * billing (billing B3), which goes on to Stripe Checkout; the rest of the
 * trial carries over.
 *
 * Founding offer (B3b): a shop a platform admin granted it (GET /api/billing
 * `founding.eligible`, B6) sees one more line; its "Choose a plan" lands on
 * the picker preselected on Core yearly with the founding discount in the
 * total (PlanTab reads the same flag). No count of the slots left: the shop
 * already holds one. Not eligible: no mention.
 */
const TrialBanner = () => {
  const { user } = useAuth();
  const owner = isOwner(user);
  const navigate = useNavigate();
  const [trial, setTrial] = useState(null);
  const [foundingOffCents, setFoundingOffCents] = useState(0);

  useEffect(() => {
    if (!owner) return undefined;
    let cancelled = false;
    planApi.get()
      .then((r) => {
        if (cancelled || !r?.success) return null;
        const t = r.data?.trial || null;
        setTrial(t);
        // Only a shop whose banner shows asks for the founding offer (Twigs and paying shops never do).
        return t && t.showBanner ? subscriptionApi.get() : null;
      })
      .then((r) => {
        const d = r?.data;
        if (!cancelled && d?.founding?.eligible && d?.catalog?.founding) setFoundingOffCents(Number(d.catalog.founding.amountOffCents) || 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [owner]);

  if (!owner || !trial || !trial.showBanner) return null;

  return (
    <div
      className="banner banner-green items-start rounded-none sm:items-center"
      role="status"
      data-testid="trial-banner"
    >
      <div className="flex flex-1 flex-col gap-1">
        <p>
          <span className="font-semibold">Your trial ends on {formatTrialEnd(trial.endsOn)}.</span>
          {' '}To keep your shop running after that, choose a plan. Nothing is charged until the trial ends.
        </p>
        {foundingOffCents > 0 && (
          <p className="flex items-center gap-1.5" data-testid="trial-banner-founding">
            <GiftIcon size={16} className="flex-shrink-0 text-floral-700" />
            Founding offer: {formatMoney(foundingOffCents)} off Core yearly, held for you
          </p>
        )}
      </div>
      <button type="button" onClick={() => navigate(CHOOSE_PLAN_PATH)} className={`${buttonClass.primary} shrink-0`}>
        Choose a plan
      </button>
    </div>
  );
};

export default TrialBanner;
